import { useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { usePublishTodo } from '@/hooks/useTodos';
import type { TodoItem, TodoPriority } from '@/lib/types';

interface TodoFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, the form edits an existing todo. */
  editTodo?: TodoItem;
}

export function TodoForm({ open, onOpenChange, editTodo }: TodoFormProps) {
  const { mutate: publishTodo, isPending } = usePublishTodo();

  const [title, setTitle] = useState(editTodo?.title ?? '');
  const [description, setDescription] = useState(editTodo?.description ?? '');
  const [priority, setPriority] = useState<TodoPriority>(editTodo?.priority ?? 'medium');
  const [date, setDate] = useState<Date | undefined>(
    editTodo?.date ? new Date(editTodo.date + 'T00:00:00') : new Date(),
  );
  const [calendarOpen, setCalendarOpen] = useState(false);

  const isEditing = !!editTodo;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !date) return;

    publishTodo(
      {
        id: editTodo?.id,
        title: title.trim(),
        date: format(date, 'yyyy-MM-dd'),
        completed: editTodo?.completed ?? false,
        description: description.trim(),
        priority,
        createdAt: editTodo?.createdAt,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          resetForm();
        },
      },
    );
  }

  function resetForm() {
    if (!editTodo) {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setDate(new Date());
    }
  }

  function handleOpenChange(newOpen: boolean) {
    onOpenChange(newOpen);
    if (!newOpen && !isEditing) {
      resetForm();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Todo' : 'New Todo'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update your encrypted to-do item.'
              : 'Create a new encrypted to-do item.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-title">Title</Label>
            <Input
              id="todo-title"
              placeholder="What needs to be done?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="todo-description">Description (optional)</Label>
            <Textarea
              id="todo-description"
              placeholder="Add more details..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-20"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex flex-col gap-2 flex-1">
              <Label>Date</Label>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left',
                      !date && 'text-muted-foreground',
                    )}
                    type="button"
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {date ? format(date, 'PPP') : 'Pick a date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setCalendarOpen(false);
                    }}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-col gap-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TodoPriority)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={isPending || !title.trim() || !date}>
              {isPending ? 'Encrypting...' : isEditing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
